/**
 * useWorkflowFolders Hook
 *
 * React Query hooks for workflow folder CRUD operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workflowFoldersApi } from '../services';
import type {
  WorkflowFolderResponse,
  WorkflowFolderListResponse,
  CreateWorkflowFolderRequest,
  UpdateWorkflowFolderRequest,
  WorkflowFolderQuery,
  WorkflowUsageType,
} from '@troia-v3/types';
import { workflowKeys } from './useWorkflows';

// Query keys — `usageType` entra na key: as árvores de Workflows e de Skills
// são separadas e não podem compartilhar cache. As formas SEM usageType são
// prefixos — `invalidateQueries` com elas atinge as duas árvores.
export const workflowFolderKeys = {
  all: ['workflow-folders'] as const,
  lists: () => [...workflowFolderKeys.all, 'list'] as const,
  list: (filters: WorkflowFolderQuery) => [...workflowFolderKeys.lists(), filters] as const,
  root: (usageType?: WorkflowUsageType) =>
    usageType
      ? ([...workflowFolderKeys.all, 'root', usageType] as const)
      : ([...workflowFolderKeys.all, 'root'] as const),
  details: () => [...workflowFolderKeys.all, 'detail'] as const,
  detail: (id: string) => [...workflowFolderKeys.details(), id] as const,
  subfolders: (parentId: string, usageType?: WorkflowUsageType) =>
    usageType
      ? ([...workflowFolderKeys.all, 'subfolders', parentId, usageType] as const)
      : ([...workflowFolderKeys.all, 'subfolders', parentId] as const),
};

/**
 * Hook to fetch folder list with pagination and filters
 */
export function useWorkflowFolders(query?: WorkflowFolderQuery) {
  return useQuery<WorkflowFolderListResponse, Error>({
    queryKey: workflowFolderKeys.list(query || {}),
    queryFn: () => workflowFoldersApi.list(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch root folders (no parent).
 * `usageType` seleciona a árvore ('automation' na tela de Workflows,
 * 'skill' na de Skills).
 */
export function useRootWorkflowFolders(usageType?: WorkflowUsageType) {
  return useQuery<WorkflowFolderResponse[], Error>({
    queryKey: workflowFolderKeys.root(usageType),
    queryFn: () => workflowFoldersApi.getRootFolders(usageType),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch single folder by ID
 */
export function useWorkflowFolder(id: string | undefined) {
  return useQuery<WorkflowFolderResponse, Error>({
    queryKey: workflowFolderKeys.detail(id!),
    queryFn: () => workflowFoldersApi.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch subfolders of a parent folder (restrito à árvore quando
 * `usageType` é passado)
 */
export function useWorkflowSubfolders(parentId: string | undefined, usageType?: WorkflowUsageType) {
  return useQuery<WorkflowFolderResponse[], Error>({
    queryKey: workflowFolderKeys.subfolders(parentId!, usageType),
    queryFn: () => workflowFoldersApi.getSubfolders(parentId!, usageType),
    enabled: !!parentId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch ancestors (breadcrumb path) for a folder
 * Returns array from root to parent folder (excludes current folder)
 */
export function useWorkflowFolderAncestors(folderId: string | null | undefined) {
  return useQuery<WorkflowFolderResponse[], Error>({
    queryKey: [...workflowFolderKeys.all, 'ancestors', folderId],
    queryFn: () => workflowFoldersApi.getAncestors(folderId!),
    enabled: !!folderId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new folder
 */
export function useCreateWorkflowFolder() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowFolderResponse, Error, CreateWorkflowFolderRequest>({
    mutationFn: (data) => workflowFoldersApi.create(data),
    onSuccess: (newFolder) => {
      // Invalidate all folder lists
      queryClient.invalidateQueries({ queryKey: workflowFolderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowFolderKeys.root() });

      // If folder has parent, invalidate parent's subfolders
      if (newFolder.parentId) {
        queryClient.invalidateQueries({
          queryKey: workflowFolderKeys.subfolders(newFolder.parentId)
        });
      }
    },
  });
}

/**
 * Hook to update a folder
 */
export function useUpdateWorkflowFolder() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowFolderResponse, Error, { id: string; data: UpdateWorkflowFolderRequest }>({
    mutationFn: ({ id, data }) => workflowFoldersApi.update(id, data),
    onSuccess: (updatedFolder) => {
      // Update the specific folder in cache
      queryClient.setQueryData(
        workflowFolderKeys.detail(updatedFolder.id),
        updatedFolder
      );
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: workflowFolderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowFolderKeys.root() });

      // Invalidate subfolders if parent changed
      if (updatedFolder.parentId) {
        queryClient.invalidateQueries({
          queryKey: workflowFolderKeys.subfolders(updatedFolder.parentId)
        });
      }
    },
  });
}

/**
 * Hook to delete a folder
 */
export function useDeleteWorkflowFolder() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; force?: boolean }>({
    mutationFn: ({ id, force }) => workflowFoldersApi.delete(id, force),
    onSuccess: (_, { id }) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: workflowFolderKeys.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: workflowFolderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowFolderKeys.root() });
      // Invalidate workflow lists in case workflows were moved
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}
